// src/pages/orders/components/bulk/BulkAssignUsers.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Users, UserCheck, X, ChevronDown, Search } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useAuth } from '../../../../hooks/useAuth';
import { getUsers } from '../../../../services/api/users';
import styles from './BulkAssignUsers.module.css';

const BulkAssignUsers = ({ selectedUserIds, onUserIdsChange, disabled }) => {
  const { t } = useLocalization();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await getUsers({ limit: 1000 });
        setUsers(response?.users || []);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest(`.${styles.dropdownWrapper}`)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isDropdownOpen]);

  const handleAssignToMe = useCallback(() => {
    if (!currentUser) return;
    if (!selectedUserIds.includes(currentUser.id)) {
      onUserIdsChange([...selectedUserIds, currentUser.id]);
    }
  }, [currentUser, selectedUserIds, onUserIdsChange]);

  const handleToggleUser = useCallback((userId) => {
    if (selectedUserIds.includes(userId)) {
      onUserIdsChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onUserIdsChange([...selectedUserIds, userId]);
    }
  }, [selectedUserIds, onUserIdsChange]);

  const handleRemoveUser = useCallback((userId, e) => {
    e.stopPropagation();
    onUserIdsChange(selectedUserIds.filter((id) => id !== userId));
  }, [selectedUserIds, onUserIdsChange]);

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUsers = users.filter((u) => selectedUserIds.includes(u.id));
  const isCurrentUserSelected = currentUser && selectedUserIds.includes(currentUser.id);

  if (isLoading) {
    return (
      <div className={styles.assignUsers}>
        <div className={styles.sectionHeader}>
          <Users size={20} />
          <h3>Assign To Users</h3>
        </div>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.assignUsers}>
      <div className={styles.sectionHeader}>
        <Users size={20} />
        <h3>Assign To Users</h3>
        <span className={styles.optionalBadge}>Optional</span>
      </div>

      <div className={styles.infoNote}>
        If users are selected, auto-assign rules will be skipped and orders will be
        distributed equally among the selected users.
      </div>

      <div className={styles.controls}>
        {/* Assign to me shortcut */}
        <button
          type="button"
          className={`${styles.assignToMeBtn} ${isCurrentUserSelected ? styles.assignToMeActive : ''}`}
          onClick={handleAssignToMe}
          disabled={disabled || isCurrentUserSelected}
          title={isCurrentUserSelected ? 'You are already selected' : 'Add yourself to the list'}
        >
          <UserCheck size={15} />
          Assign to me
        </button>

        {/* User multi-select dropdown */}
        <div className={styles.dropdownWrapper}>
          <button
            type="button"
            className={`${styles.dropdownTrigger} ${disabled ? styles.dropdownDisabled : ''}`}
            onClick={() => !disabled && setIsDropdownOpen((v) => !v)}
            disabled={disabled}
          >
            <Users size={16} className={styles.triggerIcon} />
            <span className={styles.triggerLabel}>
              {selectedUserIds.length === 0
                ? 'Select users...'
                : `${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''} selected`}
            </span>
            <ChevronDown
              size={16}
              className={`${styles.chevron} ${isDropdownOpen ? styles.chevronOpen : ''}`}
            />
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdown}>
              <div className={styles.searchBox}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                  autoFocus
                />
              </div>
              <div className={styles.userList}>
                {filteredUsers.length === 0 ? (
                  <div className={styles.noResults}>No users found</div>
                ) : (
                  filteredUsers.map((u) => {
                    const isSelected = selectedUserIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`${styles.userOption} ${isSelected ? styles.userOptionSelected : ''}`}
                        onClick={() => handleToggleUser(u.id)}
                      >
                        <div className={styles.userAvatar}>
                          {u.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>{u.name}</span>
                          {u.role?.name && (
                            <span className={styles.userRole}>{u.role.name}</span>
                          )}
                        </div>
                        {isSelected && (
                          <div className={styles.checkMark}>✓</div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected users chips */}
      {selectedUsers.length > 0 && (
        <div className={styles.selectedChips}>
          {selectedUsers.map((u) => (
            <div key={u.id} className={styles.chip}>
              <div className={styles.chipAvatar}>
                {u.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <span className={styles.chipName}>{u.name}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={(e) => handleRemoveUser(u.id, e)}
                  title={`Remove ${u.name}`}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedUsers.length === 0 && (
        <p className={styles.noSelectionNote}>
          No users selected — auto-assign rules will apply as normal.
        </p>
      )}
    </div>
  );
};

export default BulkAssignUsers;